import { ALL_REGIONS } from "../../api/constants";
import { one, scenarioKpisOrder, zero } from "../../constants";
import { Area, CommodityDataEntry, ICategoryData, IChartDataItem, ImportedDeals, IGenerateScenarioChartProps, IUpdatedRegionData, IUpdatedYearsData, IYearData, TEmissionValue, TransformedCommodityItem, IGenerateNciChartDataProps, IUpdatedCommodityData, IUpdateScenarioAndNciDataOnValueChange, IProduct } from "../../Types/types";
import { convertValue } from "./conversionutils";
import { addRecordIfNotExists, calculateNCIData, convertCarbonEmissionInMTPA, findDataItemByRegionAndCategory, findYearDataByIdAndYear, getHistoricalData, getMergedCommodity, getMergedOverllPortfolio, getUpdatedChartDataAllRegion, revertChartDataWithEmissionModification, transformCommodityData, updateChartDataWithEmissionModification } from "./util1";

const getEmissionCalculation = (emissionOne: number, emissionTwo: number, isSubstract: boolean) => isSubstract ? emissionOne - emissionTwo : emissionOne + emissionTwo;
const getIsSubstract = (isOperatingPlan: number, addToChart: number) => {
    if (isOperatingPlan === one) {
        return addToChart === one;
    } else {
        return addToChart === zero;
    }
};

export const getAddOrRemoveDeals = (
    commodityDataEntry: CommodityDataEntry[],
    movedDealsChartData: ImportedDeals[]
) => {
    if (!movedDealsChartData.length) {
        return commodityDataEntry;
    }

    return movedDealsChartData.reduce((updatedData, deal) => {
        const { TRADE_COMMODITY_NAME, CARBON_EMISSION, TRADE_YEAR, ADD_TO_CHART, IS_OPERATING_PLAN } = deal;
        const isSubstract = getIsSubstract(IS_OPERATING_PLAN, ADD_TO_CHART as number);

        return updatedData.map(commodity => {
            if (commodity.name !== TRADE_COMMODITY_NAME) {
                return commodity;
            }

            const updatedDataItems = commodity.data.map(item => {
                if (item.Year !== TRADE_YEAR) {
                    return item;
                }

                return {
                    ...item,
                    Carbon_Emissions: getEmissionCalculation(item.Carbon_Emissions, CARBON_EMISSION, isSubstract),
                };
            });

            return {
                ...commodity,
                data: updatedDataItems,
            };
        });
    }, commodityDataEntry);
};

export const getBusinessPlanTableKpisData = (categoryData: ICategoryData[] = []) => {
    const mapCategoryData = (data: ICategoryData) => {
        const { product, category, yearsdata } = data;
        return {
            id: product,
            category,
            product,
            yearsdata,
        };
    };

    const sortKpisByOrder = (kpiIdFirst: { id: string }, kpiIdSecond: { id: string }) => {
        return scenarioKpisOrder.indexOf(kpiIdFirst.id) - scenarioKpisOrder.indexOf(kpiIdSecond.id);
    };

    return categoryData?.map(mapCategoryData).sort(sortKpisByOrder);
};

export const getEmissionValue = (emissionValue: TEmissionValue) => {
    const { masterRegionData, region, subregion, category, productId, year } = emissionValue;
    let originalEmissionValue = 0;
    masterRegionData?.forEach((masterData) => {
        if (masterData.region === region && masterData.subregion === subregion) {
            masterData.categoryData.forEach((masterCategoryData) => {
                if (masterCategoryData.category === category) {
                    masterCategoryData.yearsdata.forEach((masterYearData) => {
                        if (masterYearData.id === productId && masterYearData.year === year) {
                            originalEmissionValue = masterYearData.originalValue;
                        }
                    });
                }
            });
        }
    });
    return originalEmissionValue;
}

export const getUpdatedYearsData = (updatedYearsData: IUpdatedYearsData) => {
    const { categoryData, masterRegionData, region, subregion, category, productId, year, scenerioChartData, product, setScenerioChartData } = updatedYearsData;
    return categoryData.yearsdata.map((yearData) => {
        if (yearData.id === productId && yearData.year === year) {
            const originalEmissionValue = getEmissionValue({ masterRegionData, region, subregion, category, productId, year });
            const updatedChartData = revertChartDataWithEmissionModification(
                scenerioChartData as IChartDataItem[],
                category,
                product,
                year,
                yearData.changedEmission,
                yearData.carbonIntensity,
                originalEmissionValue,
            );
            setScenerioChartData(updatedChartData as Area[]);
            return {
                ...yearData,
                changedValue: yearData.originalValue,
                changedEmission: yearData.originalEmission,
            };
        }
        return yearData;
    });
}

export const getUpdatedRegionData = (updatedRegionData: IUpdatedRegionData) => {
    const { regionData, masterRegionData, region, subregion, category, productId, year, scenerioChartData, product, setScenerioChartData } = updatedRegionData;
    return regionData?.map((data) => {
        if (data.region === region && data.subregion === subregion) {
            const updatedCategories = data.categoryData.map((categoryData) => {
                if (categoryData.category === category) {
                    const updatedYearsData = getUpdatedYearsData({ categoryData, masterRegionData, region, subregion, category, productId, year, scenerioChartData, product, setScenerioChartData });
                    return { ...categoryData, yearsdata: updatedYearsData };
                }
                return categoryData;
            });
            return { ...data, categoryData: updatedCategories };
        }
        return data;
    });
}

const getTransformedBudgetLineData = (selectedRegionPlan: TransformedCommodityItem[]) => {
    return {
        type: 'line',
        color: '#D93638',
        name: 'Total Plan',
        dashStyle: 'Dash',
        data: selectedRegionPlan,
    };
}
export const generateScenarioChartData = (
    { overAllPortfoliodata,
        importedDealsScenario,
        isReset,
        loggedInUserRegion,
        selectedRegion,
        totalPlanDatas,
        historicalStartYear,
        planningEndYear
    }: IGenerateScenarioChartProps

) => {
    const updatedSelectedRegion = isReset ? (loggedInUserRegion || ALL_REGIONS) : selectedRegion;
    const mergedOverllPortfolioData = getMergedOverllPortfolio(overAllPortfoliodata);
    const filterNonEmptyData = (x: { data: TransformedCommodityItem[] }): boolean =>
        x.data.length > 0;
    const nonemptyData =
        mergedOverllPortfolioData &&
        addRecordIfNotExists(mergedOverllPortfolioData, historicalStartYear, planningEndYear);
    const addOrRemoveDeals = getAddOrRemoveDeals(nonemptyData, importedDealsScenario);
    const transformedData =
        addOrRemoveDeals
            ?.map(item => transformCommodityData(item, historicalStartYear, planningEndYear))
            .filter(filterNonEmptyData)
            .filter((item: { name: string }) => item.name !== 'LNG');
    const mergedCommodityData = transformedData && getMergedCommodity(transformedData);
    // Added new code
    const isRegionExist = totalPlanDatas?.find((item) => item.region === updatedSelectedRegion);
    let tempTotalPlansData: {
        region: string;
        data: TransformedCommodityItem[];
    }[] = totalPlanDatas;
    if (!isRegionExist) {
        tempTotalPlansData = [
            ...(totalPlanDatas || mergedCommodityData),
            { region: updatedSelectedRegion, data: mergedCommodityData },
        ];
    }
    const selectedRegionPlan = tempTotalPlansData.find((item) => item.region === updatedSelectedRegion)?.data ?? [];
    const transformedbudgetlineData = getTransformedBudgetLineData(selectedRegionPlan);
    return { tempTotalPlansData, transformedData, transformedbudgetlineData }
}

export const generateNetCarbonIntensityChartData = ({
    inPlanDealsId,
    selectedAddToChartDeals,
    outPlanDealsId,
    importedDealsData,
}: IGenerateNciChartDataProps) => {
    let localInPlanDealsId: number[] = Array.from(new Set([...inPlanDealsId]));
    if (selectedAddToChartDeals.IS_OPERATING_PLAN === one) {
        if (selectedAddToChartDeals.ADD_TO_CHART === one) {
            if (!inPlanDealsId.includes(selectedAddToChartDeals.OPPORTUNITY_ID)) {
                localInPlanDealsId.push(selectedAddToChartDeals.OPPORTUNITY_ID);
            }
        } else {
            localInPlanDealsId = inPlanDealsId.filter((dealId) => dealId !== selectedAddToChartDeals.OPPORTUNITY_ID);
        }
    }
    const dealIdsSet = new Set([...outPlanDealsId, ...localInPlanDealsId]);
    const filteredDeals = importedDealsData.reduce<ImportedDeals[]>((acc, deal) => {
        if (dealIdsSet.has(deal.OPPORTUNITY_ID)) {
            acc.push({
                ...deal,
                ADD_TO_CHART: one,
            });
        }
        return acc;
    }, []);
    const updatedFilter = filteredDeals.map((deal) => {
        return deal.CARBON_EMISSION_UNIT === 'MTPA' ? deal : convertCarbonEmissionInMTPA(deal);
    }) as ImportedDeals[];
    return { localInPlanDealsId, filteredDeals, updatedFilter };
}

export const getUpdatedCommodityData = ({ selectedRegion, mergedOverllPortfolio, regionData, selectedRegionData }: IUpdatedCommodityData) => {
    let filterData;
    let filterRegion;
    if (selectedRegion === ALL_REGIONS) {
        filterData = mergedOverllPortfolio;
        filterRegion = regionData;
    } else {
        const regions = selectedRegion;
        filterData = mergedOverllPortfolio.map((item) => {
            return {
                ...item,
                data: item.data.filter((dataItem) => {
                    if (regions === 'EUROPE') {
                        return dataItem.Region === 'EUROPE' || dataItem.Region === 'AFRICA';
                    }
                    return dataItem.Region === regions;
                }),
            };
        });
        filterRegion = selectedRegionData && [selectedRegionData];
    }
    const allRegion = filterRegion && getUpdatedChartDataAllRegion(filterRegion);
    return allRegion && getHistoricalData(filterData, allRegion);
}

const getConvertedValue = (yearData: IYearData, newValue: number, commodityName: string | undefined, year: number) => {
    const { changedValue, unit, carbonIntensityUom, carbonIntensity } = yearData;
    const uomFirstPart = carbonIntensityUom.split('/')[0];
    const uomSecondPart = carbonIntensityUom.split('/')[one];
    const carbonVolumeinunit = convertValue(newValue, unit, uomSecondPart, commodityName);
    const emissionValueByIntensity = (carbonVolumeinunit ?? 0) * carbonIntensity;
    const carbonEmissionInMTPA = convertValue(emissionValueByIntensity, uomFirstPart, 'mtpa', commodityName);
    const deltaVolume = newValue - changedValue; // gwh
    const deltaVolumeInMj = convertValue(deltaVolume, unit, `mj${year}`, commodityName);

    const deltaVolumeInMwh = convertValue(deltaVolume, unit, 'mwh', commodityName);
    return { carbonEmissionInMTPA, deltaVolumeInMj, deltaVolumeInMwh };
}

const getUpdatedRegionDataOnValueChange = (productId: string, yearData: IYearData, regionDataCopy: IProduct[], newValue: number, carbonEmissionInMTPA: number | undefined) => {
    const updatedYearData = { ...yearData };
    updatedYearData.changedValue = newValue;
    updatedYearData.changedEmission = carbonEmissionInMTPA;
    const updateYearsData = (yearsData: IYearData[]) => {
        return yearsData.map((data) => data.id === productId ? updatedYearData : data);
    }
    const updateCategoryData = (categoryDatas: ICategoryData[]) => {
        return categoryDatas.map(categoryData => ({
            ...categoryData,
            yearsdata: updateYearsData(categoryData.yearsdata)
        }));
    };
    return regionDataCopy.map((item) => ({
        ...item,
        categoryData: updateCategoryData(item.categoryData),
    }));
}

export const generateScenarioAndNciDataOnValueChange = (scenarioAndNciDataObject: IUpdateScenarioAndNciDataOnValueChange) => {
    const { regionData, region, subregion, productId, year, category, product, newValue, nciTransformData, scenerioChartData } = scenarioAndNciDataObject;
    if (!regionData) {
        return undefined;
    }
    const regionDataCopy: IProduct[] = JSON.parse(JSON.stringify(regionData));
    const regionItem = findDataItemByRegionAndCategory(regionDataCopy, region, subregion);
    if (!regionItem) {
        return undefined;
    }
    const categoryDataCopy: ICategoryData[] = JSON.parse(JSON.stringify(regionItem.categoryData));
    const commYeardata = findYearDataByIdAndYear(categoryDataCopy, productId, year);
    const yearData = commYeardata?.yearsData;
    const commodityName = commYeardata?.commodity;
    if (!yearData) {
        return undefined;
    }
    const { originalValue, carbonIntensity, originalEmission, changedEmission } = yearData;
    const { carbonEmissionInMTPA, deltaVolumeInMj, deltaVolumeInMwh } = getConvertedValue(yearData, newValue, commodityName, year);

    const updatedNciData = calculateNCIData({
        deltaVolumeInMj: deltaVolumeInMj as number,
        deltaVolumeInMwh: deltaVolumeInMwh as number,
        year,
        carbonIntensity: carbonIntensity ?? null,
        netCarbonIntensityData: nciTransformData,
        commodityName: commodityName as string
    });
    const updatedScenarioChartData = updateChartDataWithEmissionModification({
        chartDataWithEmission: scenerioChartData as IChartDataItem[],
        category,
        product,
        year,
        originalEmission,
        changedEmission,
        emissionMTPA: carbonEmissionInMTPA,
        originalValue,
        carbonIntensity
    });
    const updatedRegionData = getUpdatedRegionDataOnValueChange(productId, yearData, regionDataCopy, newValue, carbonEmissionInMTPA);
    return { updatedNciData, updatedScenarioChartData, updatedRegionData };
}